import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getPrintAgents, createPrintAgent, deletePrintAgent, getBranches } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import SelectBox from "../components/UI/SelectBox";
import FloatingInput from "../components/UI/FloatingInput";
import ConfirmModal from "../components/UI/ConfirmModal";
import { notify } from "../utils/notify";
import DataTable from "../components/UI/DataTable";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

// AGENT_ONLINE_WINDOW_MS is how fresh PrintAgent.LastSeenAt has to be for
// this list to call it "online" — the agent polls every ~4s (see
// cmd/printagent/main.go), so anything older than a few missed polls reads
// as offline regardless of the DB's own Status column, the exact same
// "REST poll is the reliable signal, not a live push" convention
// models.Printer.LastStatus's own doc comment already documents.
const AGENT_ONLINE_WINDOW_MS = 30000;

function isAgentOnline(agent) {
  return !!agent.last_seen_at && Date.now() - new Date(agent.last_seen_at).getTime() < AGENT_ONLINE_WINDOW_MS;
}

// AddPrintAgentModal starts the pairing handshake — see
// PairPrintAgentHandler's own doc comment on the backend. The pairing code
// this shows is returned exactly once and never retrievable again, so the
// modal deliberately stays open (with a copy button) until the admin
// explicitly closes it, the same "show it once, make it easy to copy"
// pattern Checkout.js's own SellVoucherModal already uses for a freshly
// sold voucher's code.
function AddPrintAgentModal({ branchOptions, onClose, onCreated }) {
  const { t } = useTranslation("print_agents");
  const { t: tCommon } = useTranslation("common");
  const [name, setName] = useState("");
  const [branchId, setBranchId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async () => {
    if (!name.trim()) {
      notify.error(t("validation_name_required", "Agent name is required"));
      return;
    }
    if (!branchId) {
      notify.error(t("validation_branch_required", "A branch is required"));
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const created = await createPrintAgent(token, { name: name.trim(), branch_id: Number(branchId) });
      setResult(created);
      onCreated();
    } catch (err) {
      notify.error(err.data?.error || err.message || t("notify_create_failed", "Failed to create print agent"));
    } finally {
      setSubmitting(false);
    }
  };

  const copyCode = () => {
    if (!result) return;
    navigator.clipboard?.writeText(result.pairing_code).then(
      () => notify.success(t("notify_code_copied", "Pairing code copied")),
      () => {}
    );
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <div className="relative bg-white rounded-[2.5rem] p-8 max-w-md w-full shadow-2xl">
        {result ? (
          <div className="text-center">
            <ICONS.PrintAgents className="w-14 h-14 mx-auto text-emerald-500 mb-3" />
            <h3 className="text-xl font-black text-gray-900 mb-1">{t("pairing_code_title", "Pairing Code")}</h3>
            <p className="text-gray-400 text-sm mb-4">{t("pairing_code_subtitle", "Enter this code (and this server's address) into the print agent program on the branch machine — it expires in 15 minutes and is only ever shown this once")}</p>
            <div className="bg-gray-50/60 rounded-2xl p-4 mb-6 flex items-center justify-between gap-3">
              <p className="font-mono text-2xl font-black text-gray-800 tracking-widest">{result.pairing_code}</p>
              <button onClick={copyCode} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400 shrink-0">
                <ICONS.ReceiptTemplate className="w-5 h-5" />
              </button>
            </div>
            <Button variant="primary" onClick={onClose} className="w-full">{tCommon("btn_close", "Close")}</Button>
          </div>
        ) : (
          <>
            <h3 className="text-xl font-black text-gray-900 mb-1">{t("add_modal_title", "Add Print Agent")}</h3>
            <p className="text-gray-400 text-sm mb-6">{t("add_modal_subtitle", "One row per machine running the print agent program at a branch")}</p>

            <div className="space-y-5">
              <FloatingInput label={t("field_name_label", "Agent Name")} value={name} onChange={(e) => setName(e.target.value)} icon={ICONS.PrintAgents} />
              <SelectBox label={t("field_branch_label", "Branch")} options={branchOptions} value={branchId} onChange={setBranchId} />

              <div className="flex gap-3 pt-2">
                <Button variant="primary" onClick={handleSubmit} loading={submitting} className="flex-1">{t("btn_create_agent", "Generate Pairing Code")}</Button>
                <Button variant="secondary" onClick={onClose} disabled={submitting} className="flex-1">{tCommon("btn_cancel", "Cancel")}</Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function PrintAgents() {
  const { t } = useTranslation("print_agents");
  const { t: tCommon } = useTranslation("common");
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [agents, setAgents] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({ isOpen: false, id: null });
  const [status, setStatus] = useState("idle");
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const fetchAgents = async () => {
    try {
      const data = await getPrintAgents(token);
      setAgents(data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchAgents();
    getBranches(token).then(setBranches).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const branchOptions = useMemo(() => branches.map((b) => ({ value: String(b.id), label: b.name })), [branches]);

  const sortedAgents = useMemo(() => {
    const result = [...agents];
    result.sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return result;
  }, [agents, sortConfig]);

  const handleDeleteClick = (id) => setModalConfig({ isOpen: true, id });

  const confirmDelete = async () => {
    try {
      setStatus("loading");
      await deletePrintAgent(token, modalConfig.id);
      setStatus("success");
      setTimeout(() => {
        setModalConfig({ isOpen: false, id: null });
        setStatus("idle");
      }, 1200);
      notify.success(t("notify_deleted", "Print agent deleted"));
      fetchAgents();
    } catch (err) {
      setStatus("error");
      notify.error(err.data?.error || err.message || t("notify_delete_failed", "Failed to delete print agent"));
    }
  };

  const columns = [
    { key: "id", label: tCommon("table_col_id", "ID"), className: "text-gray-400 font-mono" },
    {
      key: "name",
      label: t("col_name", "Agent"),
      render: (row) => (
        <div className="flex items-center gap-3 font-bold">
          <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
            <ICONS.PrintAgents className="w-4 h-4" />
          </div>
          <div>{row.name}</div>
        </div>
      ),
    },
    { key: "branch", label: t("col_branch", "Branch"), className: "text-gray-600", render: (row) => row.branch?.name || "—" },
    { key: "printers", label: t("col_printers", "Printers"), className: "text-gray-600 font-mono", render: (row) => row.printers?.length || 0 },
    {
      key: "status",
      label: tCommon("table_col_status", "Status"),
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${isAgentOnline(row) ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>
          {isAgentOnline(row) ? t("status_online", "Online") : t("status_offline", "Offline")}
        </span>
      ),
    },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        title={t("delete_confirm_title", "Delete Print Agent")}
        message={t("delete_confirm_message", "Are you sure? This will fail if the agent still has printers assigned to it.")}
        onConfirm={confirmDelete}
        onCancel={() => setModalConfig({ isOpen: false, id: null })}
        isLoading={status === "loading"}
        isSuccess={status === "success"}
        isError={status === "error"}
      />

      {showAddModal && (
        <AddPrintAgentModal
          branchOptions={branchOptions}
          onClose={() => { setShowAddModal(false); fetchAgents(); }}
          onCreated={fetchAgents}
        />
      )}

      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.PrintAgents size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>{t("page_title", "Print Agents")}</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">{t("page_subtitle", "Machines running the print agent program, bridging this server to a branch's own printers")}</p>
          </div>
        </div>

        <Button variant="primary" icon={ICONS.Plus} onClick={() => setShowAddModal(true)} className="w-full sm:w-auto shrink-0">
          {t("btn_add_agent", "Add Print Agent")}
        </Button>
      </PageHeader>

      <DataTable
        columns={columns}
        data={sortedAgents}
        sortConfig={sortConfig}
        onSort={requestSort}
        renderActions={(row, isMobile) => (
          <Button variant="danger" size="sm" icon={ICONS.DeleteUser} onClick={() => handleDeleteClick(row.id)}>{isMobile && ` ${tCommon("btn_delete", "Delete")}`}</Button>
        )}
        mobileRender={(row) => (
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-gray-400 font-mono">{row.branch?.name}</span>
            <h3 className="font-bold text-gray-800">{row.name}</h3>
          </div>
        )}
      />
    </div>
  );
}
